import DSBox from "../../system-design/DSBox";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import { DeviceCommand, MODAL_ACTIONS_INFO } from "../../../utils/util";
import { useState } from "react";
import ScheduleBulkTaskModal from "./ScheduleBulkTaskModal";
import AddPendingKlydoDialog from "./device-config/AddPendingKlydoDialog";
import AddToBetaGroupDialog from "./AddToBetaGroupDialog";
import BatchSettingsDialog from "./BatchSettingsDialog";
import { PendingKlydo } from "../../../Types";
import firebaseService from "../../../firebase_service/firebaseService";
import { extractApiErrorMessage } from "../../../utils/util";
import { validatePendingKlydoIds } from "../../../utils/validatePendingKlydos";

export type DevicesCommandsProps = {
  devicesIds: string[];
  deviceVersion?: number;
  deviceLastVersion?: number | string;
  hardVersionValue?: string | number;
  // The selected rows, with their versions. Passed by the Devices table only:
  // it enables the batch "Change Settings" action.
  devices?: { id: string; version?: number }[];
};
const DevicesCommands = ({
  devicesIds,
  deviceVersion,
  deviceLastVersion,
  hardVersionValue,
  devices,
}: DevicesCommandsProps) => {
  const [deviceAction, setDeviceAction] = useState<DeviceCommand | null>(null);
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const [isAddingPending, setIsAddingPending] = useState(false);
  const [showBetaGroupDialog, setShowBetaGroupDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  // A bulk selection mixes regular and dev/artist clocks and the rows carry
  // no flags, so this path is sealed-only: the selection is validated in the
  // UI (deliberately — there is no server check on this write), then written
  // via the chunked firebase helper, which skips devices that already have
  // the klydo so a retry is safe.
  const handleAddPendingKlydo = async (pendingKlydos: PendingKlydo[]) => {
    setIsAddingPending(true);
    try {
      const validationError = await validatePendingKlydoIds(
        pendingKlydos.map((pk) => pk.id),
        false,
      );
      if (validationError) {
        alert(validationError);
        return;
      }

      const { succeeded, failed } =
        await firebaseService.addPendingKlydosToDevices(
          devicesIds,
          pendingKlydos,
        );
      setShowPendingDialog(false);
      if (failed.length) {
        alert(
          `Added to ${succeeded.length} of ${devicesIds.length} devices. ` +
            `${failed.length} failed — you can safely retry (devices that ` +
            `already have it are skipped).`,
        );
      }
    } catch (error) {
      // The dialog's onAdd is fire-and-forget — without this catch a failed
      // validation fetch is an unhandled rejection and the admin sees nothing.
      alert(extractApiErrorMessage(error, "Failed to add pending klydos"));
    } finally {
      setIsAddingPending(false);
    }
  };

  return (
    <>
      <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
        {Object.keys(DeviceCommand).map((key) => {
          const command: DeviceCommand =
            DeviceCommand[key as keyof typeof DeviceCommand];
          const commandInfo = MODAL_ACTIONS_INFO[command];

          // Hide commands not available for batch operations
          if (commandInfo.hideForMultiple && devicesIds.length > 1) return null;

          // Check if command should be hidden based on device version
          if (deviceVersion) {
            if (
              commandInfo.minVersion &&
              deviceVersion < commandInfo.minVersion
            )
              return null;
            if (
              commandInfo.maxVersion &&
              deviceVersion > commandInfo.maxVersion
            )
              return null;
          }

          return (
            <DSPrimaryButton
              key={key}
              size="small"
              sx={{
                fontSize: "80%",
                height: "fit-content",
                p: "6px 12px",
              }}
              onClick={() => {
                if (
                  devicesIds.length > 1 &&
                  MODAL_ACTIONS_INFO[command].disableForMultiple
                )
                  return alert(
                    `Only one device can be selected for ${command}`,
                  );
                setDeviceAction(command);
              }}
            >
              {MODAL_ACTIONS_INFO[command].headline}
            </DSPrimaryButton>
          );
        })}
        <DSSecondaryButton
          size="small"
          sx={{
            fontSize: "80%",
            height: "fit-content",
            p: "6px 12px",
          }}
          onClick={() => setShowPendingDialog(true)}
        >
          Add Pending Klydo
        </DSSecondaryButton>
        <DSSecondaryButton
          size="small"
          sx={{
            fontSize: "80%",
            height: "fit-content",
            p: "6px 12px",
          }}
          onClick={() => setShowBetaGroupDialog(true)}
        >
          Add to Beta Group
        </DSSecondaryButton>
        {devices && (
          <DSSecondaryButton
            size="small"
            sx={{
              fontSize: "80%",
              height: "fit-content",
              p: "6px 12px",
            }}
            onClick={() => setShowSettingsDialog(true)}
          >
            Change Settings
          </DSSecondaryButton>
        )}
      </DSBox>
      {!!deviceAction && (
        <ScheduleBulkTaskModal
          open={true}
          handleClose={() => {
            setDeviceAction(null);
          }}
          action={deviceAction!}
          selectedDevices={devicesIds}
          deviceVersion={deviceVersion}
          deviceLastVersion={deviceLastVersion}
          hardVersionValue={hardVersionValue}
        />
      )}
      {showBetaGroupDialog && (
        <AddToBetaGroupDialog
          open={true}
          onClose={() => setShowBetaGroupDialog(false)}
          devicesIds={devicesIds}
        />
      )}
      {showSettingsDialog && devices && (
        <BatchSettingsDialog
          open={true}
          onClose={() => setShowSettingsDialog(false)}
          devices={devices}
        />
      )}
      {showPendingDialog && (
        <AddPendingKlydoDialog
          open={true}
          onClose={() => setShowPendingDialog(false)}
          onAdd={handleAddPendingKlydo}
          existingCollections={[]}
          // A bulk selection mixes clock kinds and the rows carry no flags,
          // so this path is sealed-only (there is NO server check on this
          // write — see handleAddPendingKlydo above).
          draftsAllowed={false}
          isSaving={isAddingPending}
        />
      )}
    </>
  );
};

export default DevicesCommands;
